import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSelector from '../common/LanguageSelector';

const Header = () => {
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setMobileDrawerOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const mobileDrawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)'
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}
      >
        <Box display="flex" alignItems="center">
          <img
            src="/images/bookingbarber-logo.png"
            alt="BookingBarber"
            style={{
              height: 32,
              marginRight: 12,
              borderRadius: '18px'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #212121 0%, #666666 50%, #212121 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundSize: '200% 100%',
              animation: 'gradientShift 3s ease-in-out infinite',
              '@keyframes gradientShift': {
                '0%, 100%': {
                  backgroundPosition: '0% 50%'
                },
                '50%': {
                  backgroundPosition: '100% 50%'
                }
              }
            }}
          >
            BookingBarber
          </Typography>
        </Box>
        <IconButton onClick={handleMobileDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <List sx={{ pt: 2 }}>
        {currentUser ? (
          <>
            <ListItem
              component="button"
              onClick={() => handleNavigation('/dashboard')}
              sx={{
                py: 2,
                '&:hover': {
                  backgroundColor: 'rgba(245, 245, 220, 0.2)'
                }
              }}
            >
              <ListItemText
                primary={t('navigation.myDashboard')}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: '#212121'
                }}
              />
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem
              button
              onClick={handleLogout}
              sx={{
                py: 2,
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.1)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LogoutIcon sx={{ color: '#f44336', fontSize: 20 }} />
                <ListItemText
                  primary={t('navigation.logout')}
                  primaryTypographyProps={{
                    fontWeight: 600,
                    color: '#f44336'
                  }}
                />
              </Box>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              button
              onClick={() => handleNavigation('/auth')}
              sx={{
                py: 2,
                '&:hover': {
                  backgroundColor: 'rgba(245, 245, 220, 0.2)'
                }
              }}
            >
              <ListItemText
                primary={t('navigation.signIn')}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: '#212121'
                }}
              />
            </ListItem>
            <ListItem sx={{ px: 2, py: 2 }}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                  color: '#212121',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(245, 245, 220, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(245, 245, 220, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
                onClick={() => handleNavigation('/auth?mode=signup')}
              >
                {t('hero.startFree')}
              </Button>
            </ListItem>
          </>
        )}
        
        {/* Language Selector for Mobile */}
        <Divider sx={{ my: 2 }} />
        <ListItem sx={{ px: 2, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
              Language:
            </Typography>
            <LanguageSelector size="small" />
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'white',
          color: 'black',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar sx={{ px: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
            <img
              src="/images/bookingbarber-logo.png"
              alt="BookingBarber"
              style={{
                height: isMobile ? 32 : 40,
                marginRight: isMobile ? 8 : 16,
                borderRadius: '8px'
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                background: 'linear-gradient(135deg, #212121 0%, #666666 50%, #212121 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundSize: '200% 100%',
                animation: 'gradientShift 3s ease-in-out infinite',
                '@keyframes gradientShift': {
                  '0%, 100%': {
                    backgroundPosition: '0% 50%'
                  },
                  '50%': {
                    backgroundPosition: '100% 50%'
                  }
                }
              }}
            >
              BookingBarber
            </Typography>
          </Box>
          
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleMobileDrawerToggle}
              sx={{
                p: 1,
                '&:hover': {
                  backgroundColor: 'rgba(245, 245, 220, 0.2)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <>
              {currentUser ? (
                <>
                  <Button
                    color="inherit"
                    onClick={() => navigate('/dashboard')}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(245, 245, 220, 0.2)'
                      }
                    }}
                  >
                    {t('navigation.myDashboard')}
                  </Button>
                  <IconButton
                    onClick={handleLogout}
                    sx={{
                      ml: 1,
                      color: '#f44336',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                      }
                    }}
                    title={t('navigation.logout')}
                  >
                    <LogoutIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    color="inherit"
                    onClick={() => navigate('/auth')}
                    sx={{
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(245, 245, 220, 0.2)'
                      }
                    }}
                  >
                    {t('navigation.signIn')}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      ml: 2,
                      background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                      color: '#212121',
                      fontWeight: 700,
                      px: 3,
                      py: 1,
                      borderRadius: 3,
                      textTransform: 'none',
                      boxShadow: '0 4px 15px rgba(245, 245, 220, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 20px rgba(245, 245, 220, 0.5)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => navigate('/auth?mode=signup')}
                  >
                    {t('hero.startFree')}
                  </Button>
                </>
              )}
              
              {/* Language Selector */}
              <Box sx={{ ml: 2 }}>
                <LanguageSelector size="small" />
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 0 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        {mobileDrawer}
      </Drawer>
    </>
  );
};

export default Header;